import { ApplicationConfig, APP_INITIALIZER, inject } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors, withXsrfConfiguration, HttpInterceptorFn } from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { tokenRefreshInterceptor } from './core/interceptors/token-refresh.interceptor';
import { importProvidersFrom } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { successInterceptor } from './core/interceptors/success.interceptor';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader, TRANSLATE_HTTP_LOADER_CONFIG } from '@ngx-translate/http-loader';
import { provideRouter } from '@angular/router';
import { provideToastr } from 'ngx-toastr';
import { ToastComponent } from './shared/components/toast/toast.component';
import { AuthService } from './core/services/auth.service';
import { LanguageService } from './shared/services/language.service';
import { routes } from './app-routing.module';
import { WebMcpToolsService } from './core/webmcp/webmcp-tools.service';

export function HttpLoaderFactory() {
  return new TranslateHttpLoader();
}

// Adds withCredentials to every outgoing request
export const credentialsInterceptor: HttpInterceptorFn = (req, next) =>
  next(req.clone({ withCredentials: true }));

/**
 * On app startup:
 *  - If there is a stored user, validate the session cookie against the backend. If the
 *    cookie is still valid, keep the user logged in. If it has expired but a refresh cookie
 *    exists, the tokenRefreshInterceptor will silently renew it on the first protected API call.
 *  - Load the initial language's translations (AppComponent's whole template is gated on
 *    this being ready).
 * These two are unrelated to each other, so they run via Promise.all rather than one after
 * the other - Angular blocks bootstrap (and therefore ALL rendering, down to the empty
 * <app-root> the static index.html loader sits in front of) on every APP_INITIALIZER, so
 * running them sequentially would mean every cold load pays (session time + translation
 * time) instead of max(session time, translation time).
 */
export function initializeSession(authService: AuthService, languageService: LanguageService) {
  return async () => {
    const sessionReady = localStorage.getItem('currentUser')
      ? authService.hydrate()
      : Promise.resolve();
    await Promise.all([sessionReady, languageService.initializeLanguage()]);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideToastr({
      toastComponent: ToastComponent,
      toastClass: 'ngx-toastr app-toast-shell',
      closeButton: false,
      newestOnTop: true,
      timeOut: 5000,
      extendedTimeOut: 1000,
      positionClass: 'toast-bottom-left'
    }),
    provideHttpClient(
      withXsrfConfiguration({ cookieName: 'XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' }),
      // Interceptor order matters (errors bubble back in REVERSE order):
      // request out:  credentials → auth → error → success → tokenRefresh → HTTP
      // error back:   HTTP → tokenRefresh → success → error → auth → credentials
      // tokenRefresh must be LAST so it catches 401s before errorInterceptor does.
      // If refresh succeeds it retries silently; if it fails it re-throws so
      // errorInterceptor can redirect to login.
      withInterceptors([
        credentialsInterceptor,
        authInterceptor,
        errorInterceptor,
        successInterceptor,
        tokenRefreshInterceptor
      ])
    ),
    importProvidersFrom(
      AppRoutingModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory
        }
      })
    ),
    provideAnimations(),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: { prefix: './assets/i18n/', suffix: '.json' }
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const authService = inject(AuthService);
        const languageService = inject(LanguageService);
        return initializeSession(authService, languageService);
      },
      multi: true
    },
    {
      // Exposes create_user / create_device to in-browser AI agents (WebMCP) while signed in.
      // Synchronous and a no-op in browsers without navigator.modelContext, so it never delays bootstrap.
      provide: APP_INITIALIZER,
      useFactory: () => {
        const webMcpToolsService = inject(WebMcpToolsService);
        return () => webMcpToolsService.init();
      },
      multi: true
    }
  ]
};
